import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Package, ArrowRight, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { StatusBadge, fmtDate, btnPrimary, btnSecondary } from "@/components/common/Form";
import { EmptyState, LoadingState, ErrorState } from "@/components/common/States";

function useOrder(orderNumber) {
  const [state, setState] = useState({ order: null, error: "" });
  useEffect(() => {
    let cancelled = false;
    api.get(`/orders/${orderNumber}`).then((r) => { if (!cancelled) setState({ order: r.data, error: "" }); })
      .catch((err) => { if (!cancelled) setState({ order: null, error: formatApiError(err.response?.data?.detail) }); });
    return () => { cancelled = true; };
  }, [orderNumber]);
  return state;
}

function AddressBlock({ a }) {
  return (
    <div className="text-sm text-stone-600" data-testid="order-address">
      <div className="font-semibold text-stone-900">{a.full_name} · {a.phone}</div>
      <div>{a.address_line_1}{a.address_line_2 && `, ${a.address_line_2}`}{a.landmark && `, ${a.landmark}`}</div>
      <div>{a.city}{a.district && `, ${a.district}`}, {a.state} {a.postal_code}, {a.country}</div>
    </div>
  );
}

function OrderBody({ order }) {
  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-8">
      <div className="space-y-5">
        {order.vendors.map((g) => (
          <section key={g.id} className="rounded-xl border border-border bg-card p-5" data-testid={`order-vendor-${g.vendor_slug || g.vendor_id}`}>
            <div className="flex items-center justify-between text-sm mb-3">
              <span className="font-semibold text-stone-900">Sold by <Link to={`/store/${g.vendor_slug}`} className="text-secondary">{g.vendor_name}</Link></span>
              <StatusBadge value={g.status} />
            </div>
            <ul className="divide-y divide-border">
              {g.items.map((i) => (
                <li key={i.id} className="flex items-center gap-3 py-3 text-sm" data-testid={`order-item-${i.sku}`}>
                  <img src={i.image} alt="" className="h-14 w-14 rounded-md object-cover bg-muted" />
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${i.slug}`} className="font-medium text-stone-900 hover:text-secondary line-clamp-1">{i.product_name_snapshot}</Link>
                    <div className="text-xs text-stone-500">{i.variant_name_snapshot && `${i.variant_name_snapshot} · `}{formatINR(i.unit_price)} × {i.quantity}</div>
                  </div>
                  <div className="font-semibold text-stone-900">{formatINR(i.subtotal)}</div>
                </li>
              ))}
            </ul>
            <div className="text-right text-sm text-stone-600">Vendor subtotal <b className="text-stone-900">{formatINR(g.subtotal)}</b></div>
          </section>
        ))}
      </div>
      <aside className="space-y-5">
        <section className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-semibold text-stone-900 mb-3">Delivery address</h3>
          <AddressBlock a={order.delivery_address_snapshot} />
        </section>
        <section className="rounded-xl border border-border bg-card p-5 text-sm text-stone-600 space-y-2" data-testid="order-summary">
          <div className="flex justify-between"><span>Subtotal</span><span className="font-medium text-stone-800">{formatINR(order.subtotal)}</span></div>
          <div className="flex justify-between"><span>Shipping</span><span className="font-medium text-stone-800">{formatINR(order.shipping_amount)}</span></div>
          <div className="flex justify-between"><span>Tax</span><span className="font-medium text-stone-800">{formatINR(order.tax_amount)}</span></div>
          <div className="border-t border-border pt-3 flex justify-between font-bold text-lg text-stone-900"><span>Total</span><span className="text-secondary" data-testid="order-total">{formatINR(order.total_amount)}</span></div>
          <div className="pt-2 flex items-center justify-between"><span>Order status</span><StatusBadge value={order.status} testId="order-status" /></div>
          <div className="flex items-center justify-between"><span>Payment status</span><StatusBadge value={order.payment_status} testId="order-payment-status" /></div>
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-2" data-testid="order-payment-note">Payment gateway not yet integrated — payment is pending.</p>
        </section>
      </aside>
    </div>
  );
}

export function OrderConfirmation() {
  const { orderNumber } = useParams();
  const { order, error } = useOrder(orderNumber);
  if (error) return <ErrorState message={error} />;
  if (!order) return <LoadingState />;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="order-confirmation-page">
      <div className="rounded-2xl bg-card border border-border p-6 sm:p-8 mb-8 text-center">
        <CheckCircle2 className="mx-auto text-emerald-600 mb-3" size={40} />
        <span className="eyebrow">Order placed</span>
        <h1 className="mt-2 text-3xl font-serif font-bold text-stone-900">Thank you for your order!</h1>
        <p className="mt-2 text-stone-600">Order <b data-testid="confirmation-order-number">{order.order_number}</b> · {fmtDate(order.created_at)}</p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Link to="/shop" className={btnSecondary} data-testid="confirmation-continue">Continue Shopping</Link>
          <Link to="/orders" className={btnPrimary} data-testid="confirmation-view-orders">View Orders <ArrowRight size={14} /></Link>
        </div>
      </div>
      <OrderBody order={order} />
    </div>
  );
}

export function OrderDetail() {
  const { orderNumber } = useParams();
  const { order, error } = useOrder(orderNumber);
  if (error) return <ErrorState message={error} />;
  if (!order) return <LoadingState />;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="order-detail-page">
      <Link to="/orders" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-secondary mb-4"><ArrowLeft size={14} /> All orders</Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-serif font-bold text-stone-900" data-testid="order-detail-number">{order.order_number}</h1>
          <p className="text-sm text-stone-500">Placed on {fmtDate(order.created_at)}</p>
        </div>
      </div>
      <OrderBody order={order} />
    </div>
  );
}

export function Orders() {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/orders").then((r) => { if (!cancelled) setOrders(r.data); }).catch(() => { if (!cancelled) setOrders([]); });
    return () => { cancelled = true; };
  }, []);
  if (orders === null) return <LoadingState />;
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="orders-page">
      <h1 className="text-3xl font-serif font-bold text-stone-900 mb-6">My Orders</h1>
      {orders.length === 0 ? (
        <EmptyState title="No orders yet" message="Your orders will appear here once you place them." action={<Link to="/shop" className={btnPrimary}>Start Shopping</Link>} />
      ) : (
        <ul className="space-y-3" data-testid="orders-list">
          {orders.map((o) => (
            <li key={o.id}>
              <Link to={`/orders/${o.order_number}`} data-testid={`order-row-${o.order_number}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors">
                <div className="h-11 w-11 rounded-full bg-accent grid place-items-center text-secondary shrink-0"><Package size={20} /></div>
                <div className="flex-1 min-w-[180px]">
                  <div className="font-semibold text-stone-900">{o.order_number}</div>
                  <div className="text-xs text-stone-500">{fmtDate(o.created_at)} · {o.item_count} item(s) · {o.vendors.map((v) => v.vendor_name).join(", ")}</div>
                </div>
                <div className="flex items-center gap-2"><StatusBadge value={o.status} /><StatusBadge value={o.payment_status} /></div>
                <div className="font-bold text-secondary">{formatINR(o.total_amount)}</div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
